// ProductPreview - page-by-page reading preview (book excerpt scans, cover
// images, text excerpts) inside the fullscreen preview modal. Behavioral port
// of v1 modules/ProductPreview.js; the HTML contract (data-product-preview,
// -slider, -slide, -page, -total, -prev, -next) is unchanged, because unlike
// the gallery and the autocomplete this one really is in production use in the
// consumer's templates (and its PDF viewer reads the same attributes).
//
// v1 leaned on two vendor libraries, and ProductPreview was the only user of
// both: `scrollmonitor` (last release 2017) for "which page am I on" and
// `animated-scroll-to` for the paging animation. Same call as tippy ->
// floating-ui and gumshoe -> our own code: IntersectionObserver and the native
// element.scrollTo({ behavior: 'smooth' }) replace them, so both dependencies
// are gone. See docs/porting.md for the v1 bugs fixed along the way - the short
// version is that pages taller than the viewport never advanced the counter,
// and the keyboard handler paged the preview while you were typing anywhere on
// the page.
//
// HTML contract:
//   <div class="product-preview" data-product-preview>
//     <div class="product-preview__control">
//       <button data-product-preview-prev>...</button>
//       <p><span data-product-preview-page>1</span> / <span data-product-preview-total>5</span></p>
//       <button data-product-preview-next>...</button>
//     </div>
//     <div class="product-preview__slider" data-product-preview-slider>
//       <div class="product-preview__page" data-product-preview-slide="0">...</div>
//     </div>
//   </div>
//
// Everything but the container and the slider is optional: the control column
// is hidden on mobile (.show-m) and the Issuu / PDF preview variants render no
// slider at all, in which case the container is skipped.

export type ProductPreviewLanguage = 'sk' | 'cz';

export interface ProductPreviewTexts {
  prev: string;
  next: string;
}

export interface ProductPreviewConfig {
  /** Scrollable page container inside the preview. */
  selectorSlider?: string;
  /** One page inside the slider. */
  selectorSlide?: string;
  /** Element receiving the current page number. */
  selectorCurrentPage?: string;
  /** Element receiving the page count. */
  selectorTotalPages?: string;
  selectorNextPage?: string;
  selectorPrevPage?: string;
  /** Arrow keys / PageUp / PageDown / Space page the preview. */
  keyboard?: boolean;
  /** Toggle the `disabled` attribute on prev/next at the first/last page. */
  disableAtEdges?: boolean;
  /** UI language for the generated aria-labels; defaults to <html lang>. */
  language?: ProductPreviewLanguage;
}

export interface ProductPreviewChangeDetail {
  /** Zero-based page index. */
  index: number;
  /** Human-facing page number (index + 1). */
  page: number;
  total: number;
}

/** Fired on the container after the current page changed. */
export const PRODUCT_PREVIEW_CHANGE_EVT = 'martinus:product-preview:change';

const defaultConfig: ProductPreviewConfig = {
  selectorSlider: '[data-product-preview-slider]',
  selectorSlide: '[data-product-preview-slide]',
  selectorCurrentPage: '[data-product-preview-page]',
  selectorTotalPages: '[data-product-preview-total]',
  selectorNextPage: '[data-product-preview-next]',
  selectorPrevPage: '[data-product-preview-prev]',
  keyboard: true,
  disableAtEdges: true,
};

const texts = {
  sk: {
    prev: 'Predchádzajúca strana',
    next: 'Ďalšia strana',
  },
  cz: {
    prev: 'Předchozí strana',
    next: 'Další strana',
  },
} satisfies Record<ProductPreviewLanguage, ProductPreviewTexts>;

/** Resolve the default language from <html lang>, as in modules/select.ts. */
const detectLanguage = (): ProductPreviewLanguage => {
  const lang = document.documentElement.lang.toLowerCase();
  return lang.startsWith('cs') || lang.startsWith('cz') ? 'cz' : 'sk';
};

// Ratios the observer reports at. Enough steps to pick the dominant page even
// when a single scan is several viewports tall.
const THRESHOLDS = [0, 0.1, 0.25, 0.5, 0.75, 1];

const isEditable = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement &&
  (target.isContentEditable || /^(input|textarea|select)$/i.test(target.tagName));

/**
 * Is the element rendered? Gates the document-level key handler, so the
 * closed preview modal does not swallow the page's arrow keys. Prefers the
 * native check and falls back to walking computed styles (jsdom has no
 * layout, so offsetParent/getClientRects would report everything as hidden).
 */
const isVisible = (element: HTMLElement): boolean => {
  if (typeof element.checkVisibility === 'function') {
    return element.checkVisibility();
  }

  let current: Element | null = element;

  while (current instanceof HTMLElement) {
    const style = window.getComputedStyle(current);

    if (style.display === 'none' || style.visibility === 'hidden') {
      return false;
    }

    current = current.parentElement;
  }

  return true;
};

const instances = new WeakMap<HTMLElement, ProductPreviewInstance>();

/** One preview container. */
export class ProductPreviewInstance {
  element: HTMLElement;

  slider: HTMLElement;

  config: ProductPreviewConfig;

  texts: ProductPreviewTexts;

  slides: HTMLElement[] = [];

  private currentIndex = 0;

  private prevButton: HTMLElement | null = null;

  private nextButton: HTMLElement | null = null;

  private currentPageEl: HTMLElement | null = null;

  private totalPagesEl: HTMLElement | null = null;

  private observer: IntersectionObserver | null = null;

  /** How much of the slider each page currently covers. */
  private ratios = new Map<HTMLElement, number>();

  constructor(element: HTMLElement, slider: HTMLElement, config: ProductPreviewConfig = {}) {
    this.element = element;
    this.slider = slider;
    this.config = { ...defaultConfig, ...config };
    this.texts = texts[this.config.language ?? detectLanguage()];

    this._init();
  }

  /** Zero-based index of the page currently in view. */
  get page(): number {
    return this.currentIndex;
  }

  get total(): number {
    return this.slides.length;
  }

  /** Scroll to a page. Out-of-range indexes are clamped. */
  setPage(index: number): void {
    const target = Math.min(Math.max(index, 0), this.slides.length - 1);
    const slide = this.slides[target];

    if (!slide) {
      return;
    }

    // Optimistic: the observer confirms it a frame later, but the counter
    // should react to the click immediately (v1 did the same).
    this._setCurrent(target);

    const top =
      this.slider.scrollTop +
      slide.getBoundingClientRect().top -
      this.slider.getBoundingClientRect().top;

    // Same reasoning as in modules/smooth-scroll.ts: the vendor's easing and
    // speed knobs have no native equivalent, and prefers-reduced-motion gets
    // an instant jump.
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (typeof this.slider.scrollTo === 'function') {
      this.slider.scrollTo({ top, behavior: reduceMotion ? 'auto' : 'smooth' });
    } else {
      this.slider.scrollTop = top;
    }
  }

  next(): void {
    if (this.currentIndex < this.slides.length - 1) {
      this.setPage(this.currentIndex + 1);
    }
  }

  prev(): void {
    if (this.currentIndex > 0) {
      this.setPage(this.currentIndex - 1);
    }
  }

  /** Re-collect the pages (call after pages were added or removed). */
  update(): void {
    this._unobserve();
    this._collect();
    this._observe();
    this._syncControls();
  }

  destroy(): void {
    this._unobserve();

    this.prevButton?.removeEventListener('click', this._onPrevClick);
    this.nextButton?.removeEventListener('click', this._onNextClick);
    document.removeEventListener('keydown', this._onKeydown);

    this.prevButton = null;
    this.nextButton = null;
    this.currentPageEl = null;
    this.totalPagesEl = null;
    this.slides = [];
  }

  private _init(): void {
    this.prevButton = this.element.querySelector<HTMLElement>(
      this.config.selectorPrevPage as string,
    );
    this.nextButton = this.element.querySelector<HTMLElement>(
      this.config.selectorNextPage as string,
    );
    this.currentPageEl = this.element.querySelector<HTMLElement>(
      this.config.selectorCurrentPage as string,
    );
    this.totalPagesEl = this.element.querySelector<HTMLElement>(
      this.config.selectorTotalPages as string,
    );

    this._collect();

    // v1 called addEventListener straight on the query result, so a preview
    // without the control column (mobile, PDF/Issuu variants) threw.
    this.prevButton?.addEventListener('click', this._onPrevClick);
    this.nextButton?.addEventListener('click', this._onNextClick);

    this._labelControls();
    this._markCounterLive();
    this._observe();
    this._syncControls();

    if (this.config.keyboard !== false) {
      document.addEventListener('keydown', this._onKeydown);
    }
  }

  private _collect(): void {
    // Index comes from DOM order, not from the data-product-preview-slide
    // value: v1 parsed the number out of the attribute (deriving the attribute
    // name from the selector with a slice(1, -1) on it), which made hand-written
    // numbering part of the contract and broke outright if the selector was
    // overridden. The attribute stays valid, it is just no longer authoritative.
    this.slides = [...this.slider.querySelectorAll<HTMLElement>(this.config.selectorSlide as string)];

    if (this.currentIndex > this.slides.length - 1) {
      this.currentIndex = Math.max(this.slides.length - 1, 0);
    }

    // v1 never wrote the total - the template hardcoded count($pages) - so a
    // preview whose pages arrive from JS showed the wrong count.
    if (this.totalPagesEl) {
      this.totalPagesEl.textContent = String(this.slides.length);
    }
  }

  private _observe(): void {
    if (typeof IntersectionObserver !== 'function' || this.slides.length === 0) {
      return;
    }

    // v1 used scrollmonitor's fullyEnterViewport, which only fires once a page
    // fits inside the slider *entirely*. A scan taller than the viewport - the
    // normal case on desktop - therefore never advanced the counter. Here the
    // page covering most of the slider wins, whatever its height.
    this.observer = new IntersectionObserver(this._onIntersect, {
      root: this.slider,
      threshold: THRESHOLDS,
    });

    this.slides.forEach(slide => this.observer?.observe(slide));
  }

  private _unobserve(): void {
    this.observer?.disconnect();
    this.observer = null;
    this.ratios.clear();
  }

  private _onIntersect = (entries: IntersectionObserverEntry[]): void => {
    entries.forEach(entry => {
      this.ratios.set(entry.target as HTMLElement, entry.isIntersecting ? entry.intersectionRatio : 0);
    });

    let bestIndex = -1;
    let bestRatio = 0;

    this.slides.forEach((slide, index) => {
      const ratio = this.ratios.get(slide) ?? 0;

      // Strictly greater, so an earlier page wins a tie - matches the reading
      // direction when two pages are equally visible.
      if (ratio > bestRatio) {
        bestRatio = ratio;
        bestIndex = index;
      }
    });

    if (bestIndex >= 0) {
      this._setCurrent(bestIndex);
    }
  };

  private _setCurrent(index: number): void {
    if (index === this.currentIndex) {
      this._syncControls();
      return;
    }

    this.currentIndex = index;
    this._syncControls();

    this.element.dispatchEvent(
      new CustomEvent<ProductPreviewChangeDetail>(PRODUCT_PREVIEW_CHANGE_EVT, {
        bubbles: true,
        detail: { index, page: index + 1, total: this.slides.length },
      }),
    );
  }

  private _syncControls(): void {
    if (this.currentPageEl) {
      // textContent, not innerHTML as in v1 - it is a number, not markup.
      this.currentPageEl.textContent = String(this.slides.length ? this.currentIndex + 1 : 0);
    }

    if (this.config.disableAtEdges === false) {
      return;
    }

    // v1 left both buttons enabled forever and silently ignored the clicks at
    // the ends. The consumer's PDF viewer, written later against the same
    // markup, already disables them - v2 makes the styleguide behave the same.
    this._setDisabled(this.prevButton, this.currentIndex <= 0);
    this._setDisabled(this.nextButton, this.currentIndex >= this.slides.length - 1);
  }

  private _setDisabled(button: HTMLElement | null, disabled: boolean): void {
    if (!button) {
      return;
    }

    if (button instanceof HTMLButtonElement) {
      button.disabled = disabled;
    } else {
      // An <a> cannot be disabled; .is-disabled is the styleguide's own hook.
      button.classList.toggle('is-disabled', disabled);
      button.setAttribute('aria-disabled', String(disabled));
    }
  }

  /** Name the icon-only buttons - in v1 a screen reader read out nothing. */
  private _labelControls(): void {
    ([[this.prevButton, this.texts.prev], [this.nextButton, this.texts.next]] as const).forEach(
      ([button, label]) => {
        if (!button || button.getAttribute('aria-label') || button.textContent?.trim()) {
          return;
        }

        button.setAttribute('aria-label', label);
      },
    );
  }

  /**
   * Announce page changes. The live region goes on the wrapper holding both
   * numbers when there is one, so it reads "3 / 12" rather than a bare "3".
   */
  private _markCounterLive(): void {
    const { currentPageEl, totalPagesEl } = this;

    if (!currentPageEl) {
      return;
    }

    const parent = currentPageEl.parentElement;
    const region =
      totalPagesEl && parent?.contains(totalPagesEl) ? parent : currentPageEl;

    if (region.hasAttribute('aria-live')) {
      return;
    }

    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
  }

  private _onPrevClick = (e: Event): void => {
    e.preventDefault();
    this.prev();
  };

  private _onNextClick = (e: Event): void => {
    e.preventDefault();
    this.next();
  };

  private _onKeydown = (e: KeyboardEvent): void => {
    // v1 bound this on window and never removed it - not even in destroy() -
    // and it acted no matter whether the preview was open or where the focus
    // was. Space in any textarea, or an arrow key in any input, paged the
    // preview; Space did it *and* scrolled the page, because nothing called
    // preventDefault.
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) {
      return;
    }

    if (isEditable(e.target) || !isVisible(this.element)) {
      return;
    }

    switch (e.code) {
      case 'ArrowDown':
      case 'ArrowRight':
      case 'PageDown':
        this.next();
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
      case 'PageUp':
        this.prev();
        break;
      // v1 mapped Space/Shift+Space too; Home/End are new.
      case 'Space':
        if (e.shiftKey) this.prev();
        else this.next();
        break;
      case 'Home':
        this.setPage(0);
        break;
      case 'End':
        this.setPage(this.slides.length - 1);
        break;
      default:
        return;
    }

    e.preventDefault();
  };
}

export default class ProductPreview {
  selector: string;

  config: ProductPreviewConfig;

  elements: ProductPreviewInstance[] = [];

  constructor(selector = '[data-product-preview]', config: ProductPreviewConfig = {}) {
    this.selector = selector;
    this.config = config;

    this.elements = this._initialize();
  }

  /** The preview belonging to a container element, if it was initialized. */
  static getInstance(element: HTMLElement | null): ProductPreviewInstance | null {
    return (element && instances.get(element)) || null;
  }

  /** Re-scan the DOM for previews added since init (the modal loads by AJAX). */
  update(): void {
    this.destroy();
    this.elements = this._initialize();
  }

  destroy(): void {
    this.elements.forEach(instance => {
      instances.delete(instance.element);
      instance.destroy();
    });
    this.elements = [];
  }

  private _initialize(): ProductPreviewInstance[] {
    const selectorSlider = this.config.selectorSlider ?? (defaultConfig.selectorSlider as string);

    // v1 used document.querySelector, so only the first preview on the page was
    // ever initialized, and a container without a slider threw on null.
    return [...document.querySelectorAll<HTMLElement>(this.selector)].reduce<
      ProductPreviewInstance[]
    >((created, element) => {
      const slider = element.querySelector<HTMLElement>(selectorSlider);

      if (!slider) {
        return created;
      }

      const instance = new ProductPreviewInstance(element, slider, this.config);
      instances.set(element, instance);
      created.push(instance);

      return created;
    }, []);
  }
}
